// Card «Lo que se financió»: un renglón por línea a crédito y el total. Sin líneas, total y equipo.
import { useState } from 'react';
import type { SaleLine } from '@/api/services/retail-wizard/contract';
import { SalePromotionPrice, SalePromotionTag } from '@/shared/patterns/SalePromotion';
import type { CreditFileBlock } from '../api/useCreditFile';
import {
    COLLAPSED_COUNT,
    financedLinesOf,
    MAX_VISIBLE_LINES,
    type FinancedLineView,
} from '../financedLines';
import { formatMoneyCents } from '../logic';
import { CreditCard, CreditKeyValue } from './CreditFileUi';

/** Todos los renglones miden lo mismo: la línea de la etiqueta se reserva aunque no haya. */
function FinancedLineRow({ line }: { line: FinancedLineView }) {
    const discounted = line.originalCents !== null || line.promotion !== undefined;
    return (
        <div
            data-testid="financed-line"
            className="flex h-[56px] items-start justify-between gap-3 border-b border-border/40 py-[5px]"
        >
            <div className="min-w-0">
                <div className="truncate text-[11.5px] font-semibold" title={line.name}>
                    {line.name}
                </div>
                <div
                    className="truncate font-mono text-[10px] text-muted-foreground"
                    title={line.sku}
                >
                    {line.sku} · {line.kind}
                </div>
                <div className="h-[16px] leading-none">
                    {line.promotion ? (
                        <SalePromotionTag
                            {...(line.promotion.code !== undefined && {
                                code: line.promotion.code,
                            })}
                        />
                    ) : null}
                </div>
            </div>
            <div className="shrink-0 text-right text-[11.5px] font-semibold tabular-nums">
                {discounted ? (
                    <SalePromotionPrice
                        originalCents={line.originalCents}
                        netCents={line.netCents}
                        className="flex flex-col items-end"
                    />
                ) : (
                    formatMoneyCents(line.netCents)
                )}
            </div>
        </div>
    );
}

export function CreditFinancedCard({
    totalCents,
    productName,
    saleLines,
}: {
    /** El `equipmentPriceCents` del crédito: la suma de lo que entró al crédito. */
    totalCents: number;
    /** Nombre del equipo para cuando las líneas no se pueden leer. */
    productName: string;
    saleLines?: CreditFileBlock<SaleLine[] | null>;
}) {
    const [expanded, setExpanded] = useState(false);
    const lines = saleLines?.data ? financedLinesOf(saleLines.data) : [];
    const collapsible = lines.length > MAX_VISIBLE_LINES;
    const shown = collapsible && !expanded ? lines.slice(0, COLLAPSED_COUNT) : lines;
    const hidden = lines.length - COLLAPSED_COUNT;

    return (
        <CreditCard title="Lo que se financió">
            {lines.length === 0 ? (
                <CreditKeyValue label="Equipo" value={productName} />
            ) : (
                <>
                    {shown.map((line) => (
                        <FinancedLineRow key={line.lineId} line={line} />
                    ))}
                    {collapsible ? (
                        <div className="border-b border-border/40 py-[5px]">
                            <button
                                type="button"
                                aria-expanded={expanded}
                                className="text-[11px] font-semibold text-primary underline underline-offset-2"
                                onClick={() => {
                                    setExpanded((v) => !v);
                                }}
                            >
                                {expanded ? 'Ver menos' : `Ver ${String(hidden)} más`}
                            </button>
                        </div>
                    ) : null}
                </>
            )}
            <CreditKeyValue label="Total a crédito" value={formatMoneyCents(totalCents)} />
        </CreditCard>
    );
}
